import React, { useState, useEffect, useContext } from "react";
import api, { reportsApi } from "../../../../API/axiosInstance";
import {
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { Pagination } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"; // ✅ add this

import "../../../../styles/FakeNewsMain.css";
import NavBar from "../../../Admin/Dashboard/NavBar";
import ReactProSidebar from "../../../Admin/Dashboard/ReactProSidebar";
import placeholder from "../../../../images/Image-not-found.png";
import BasicDateTimeRangePicker from "../../../Filters/DateTimeRange";

import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import dayjs from "dayjs";
import { TranslateSwitch } from "./FakeUtils/Toggle";
import { generateHash } from "../../../../API/hashUtil";
import PrintAdvanceFactCheckReport from "../../../GeneratePdf/PrintAdvanceFactCheckReport";
import FakeNewsSkeletonCard from "./FakeNewsSkeletonCard";
import FakeNewsCard from "./FakeNewsCard";
import CustomPagination from "./FakeUtils/CustomPagination";
import CustomAlertDialog from "../../../Dialog";
import FakeNewsModal from "./FakeNewsModal";
import { MyContext } from "../../../../MyContextApi";
import DateRangeModalPicker from "../../../Filters/GlobalDateRange";

const FakeNews = () => {
  const [source, setSource] = useState("");
  const [input, setInput] = useState("");
  const [data, setData] = useState([]);
  const [checkType, setCheckType] = useState("shallow");
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [serverResponses, setServerResponses] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clearDates, setClearDates] = useState(false);
  const [fromDateTimeFormatted, setFromDateTimeFormatted] = useState(null);
  const [toDateTimeFormatted, setToDateTimeFormatted] = useState(null);
  const [translateValue, setTranslateValue] = useState(false);
  const [factLoading, setFactLoading] = useState(false);
  const [dateLabel, setDateLabel] = useState(null);
  const [loader, setLoader] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  // Add word count state
  const [wordCount, setWordCount] = useState(0);
  const [isWordLimitExceeded, setIsWordLimitExceeded] = useState(false);

  // New state for report dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    title: "",
    message: "",
    iconType: "info",
    onClose: null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showReportControls, setShowReportControls] = useState(false);

  const { fakeNewsLoading, setfakeNewsLoading } = useContext(MyContext);
  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };
  // Add function to count words
  const countWords = (text) => {
    if (!text.trim()) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  // Handle textarea input change with word limit validation
  const handleInputChange = (e) => {
    const newInput = e.target.value;
    const wordCount = countWords(newInput);

    setWordCount(wordCount);

    if (wordCount > 40) {
      setIsWordLimitExceeded(true);
      // Removed dialog box - now only showing inline error
    } else {
      setIsWordLimitExceeded(false);
    }

    setInput(newInput);
  };

  // Individual report generation function
  const generateIndividualReport = async (item) => {
    if (!item) {
      setDialogConfig({
        open: true,
        title: "Error",
        message: "No item data available for report generation",
        iconType: "error",
        onClose: () => setDialogConfig((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    try {
      const reportDate =
        toDateTimeFormatted || dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]");

      PrintAdvanceFactCheckReport(item, setFactLoading, reportDate);
    } catch (error) {
      console.error("Error generating individual report:", error);
      setDialogConfig({
        open: true,
        title: "Report Generation Error",
        message: "Error generating report for this post. Please try again.",
        iconType: "error",
        onClose: () => setDialogConfig((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleTranslateChange = (value) => {
    setTranslateValue(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchData = async (page) => {
    try {
      setLoader(true);
      const token = localStorage.getItem("Token");
      const stringParams = {
        page: page ? page.toString() : "",
      };

      const hexHash = generateHash(stringParams);
      const response = await api.post(
        `/fakenews/get-advance-fact-check-data`,
        { page },
        {
          params: {
            HPPcode: hexHash,
          },
          headers: {
            "X-XSS-Protection": "1; mode=block",
            Authorization: `Bearer ${token}`,
            "X-Content-Type-Options": "nosniff",
            "X-Frame-Options": "DENY",
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
            "Content-Type": "application/json",
          },
        }
      );
      const parsedData =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;

      if (parsedData && parsedData.data && Array.isArray(parsedData.data)) {
        const sortedData = parsedData.data.sort(
          (a, b) => new Date(b.dateTime) - new Date(a.dateTime)
        );
        setLoader(false);
        setData(sortedData);
        setCurrentPage(parsedData.current_page);
        setTotalPages(parsedData.total_pages);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching fake news data:", error);
      setData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const generateFakeNewsReport = async () => {
    try {
      setFactLoading(true);

      const token = localStorage.getItem("Token");

      const stringParams = {
        startDateStr: fromDateTimeFormatted
          ? fromDateTimeFormatted.toString()
          : "",
        endDateStr: toDateTimeFormatted ? toDateTimeFormatted.toString() : "",
        type: "fake",
      };

      const hexHash = generateHash(stringParams);

      const response = await reportsApi.post(
        `/fake-news`,
        {
          startDate: stringParams.startDateStr,
          endDate: stringParams.endDateStr,
          type: "fake",
        },
        {
          params: { HPPcode: hexHash },
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",

            // Security headers (fine to keep)
            "X-XSS-Protection": "1; mode=block",
            "X-Content-Type-Options": "nosniff",
            "X-Frame-Options": "DENY",
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
          },
        }
      );

      // ✅ Axios header access
      const cd = response.headers["content-disposition"];

      const filename =
        cd?.match(/filename="?([^"]+)"?/)?.[1] ||
        `CSMART_Fake_News_Report_${new Date().toISOString().slice(0, 10)}.pdf`;

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      // Reset UI state
      setShowReportControls(false);
      setFromDateTimeFormatted(null);
      setToDateTimeFormatted(null);
      setClearDates(true);
      setTimeout(() => setClearDates(false), 0);
    } catch (error) {
      console.error("Error generating fake news report:", error);
    } finally {
      setFactLoading(false);
    }
  };

  const analyzeFakeNews = async () => {
    // Check word limit before making API call
    if (isWordLimitExceeded || wordCount > 40) {
      // No dialog box - the error is already shown inline
      return; // Don't make API call
    }

    if (!input.trim()) {
      setDialogConfig({
        open: true,
        title: "Input Required",
        message: "Please enter text to analyze",
        iconType: "warning",
        onClose: () => setDialogConfig((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    setfakeNewsLoading(true);
    try {
      const requestBody = {
        claim: input,
        translate: translateValue.toString(),
      };

      const token = localStorage.getItem("Token");
      const stringParams = {
        claim: input ? input.toString() : "",
        translate: translateValue.toString(),
      };

      const hexHash = generateHash(stringParams);
      const response = await api.post(
        `/fakenews/advance-fact-check`,
        requestBody,
        {
          params: {
            HPPcode: hexHash,
          },
          headers: {
            "X-XSS-Protection": "1; mode=block",
            Authorization: `Bearer ${token}`,
            "X-Content-Type-Options": "nosniff",
            "X-Frame-Options": "DENY",
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
            "Content-Type": "application/json",
          },
        }
      );

      setAnalysisResult(response.data);
      setDialogConfig({
        open: true,
        title: "Analysis Complete",
        message:
          typeof response.data === "string"
            ? response.data
            : "Fact-check analysis completed successfully",
        iconType: "success",
        onClose: () => setDialogConfig((prev) => ({ ...prev, open: false })),
      });
      if (response.status === 200) {
        fetchData(currentPage);
      }
    } catch (error) {
      console.error("Error analyzing fake news:", error);
      setDialogConfig({
        open: true,
        title: "Analysis Error",
        message: "Error analyzing text. Please try again.",
        iconType: "error",
        onClose: () => setDialogConfig((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setfakeNewsLoading(false);
    }
  };

  const truncateToWordLimit = (text = "", limit = 40) => {
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= limit) return text;
    return words.slice(0, limit).join(" ") + "...";
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Invalid Date";
    }
  };

  const handleFromDateTimeChange = (fromDateTime) => {
    const formattedFromDateTime = dayjs(fromDateTime).format(
      "YYYY-MM-DDTHH:mm:ss[Z]"
    );
    setFromDateTimeFormatted(formattedFromDateTime);
  };

  const handleToDateTimeChange = (toDateTime) => {
    const formattedToDateTime = dayjs(toDateTime).format(
      "YYYY-MM-DDTHH:mm:ss[Z]"
    );
    setToDateTimeFormatted(formattedToDateTime);
  };

  return (
    <div className="container">
      <NavBar />


      <CustomAlertDialog
        open={dialogConfig.open}
        onClose={dialogConfig.onClose}
        title={dialogConfig.title}
        message={dialogConfig.message}
        iconType={dialogConfig.iconType}
      />
      <DateRangeModalPicker
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        onFromDateTimeChange={handleFromDateTimeChange}
        onToDateTimeChange={handleToDateTimeChange}
        clearDates={clearDates}
      />
      <main className="fn-verification-container">
        <div className="fn-header-container">
          <h1 className="basic-Header">Fake News</h1>

          <div className="fn-report-button-section">
            {!showReportControls ? (
              // STEP 1: Initial green button
              <button
                className="fn-top-button"
                disabled={factLoading}
                onClick={() => setShowReportControls(true)}
              >
                {factLoading ? (
                  <>
                    Generating Report...
                    <div className="loading-dots">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                  </>
                ) : (
                  <>Generate Bulk Report</>
                )}
              </button>
            ) : (
              // STEP 2: After click – show calendar icon + download button
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "white",
                  borderRadius: "30px",
                  padding: "10px 17px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Tooltip title="Select Date Range" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setDateModalOpen(true)}
                    disabled={factLoading}
                    sx={{
                      borderRadius: "50%",
                      backgroundColor: "#f0f4ff",
                      color: "#1976d2",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "#e3eaff",
                        transform: "scale(1.05)",
                      },
                      "&:disabled": {
                        backgroundColor: "#f5f5f5",
                        color: "#bdbdbd",
                      },
                    }}
                  >
                    <CalendarMonthIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Button
                  onClick={generateFakeNewsReport}
                  variant="contained"
                  size="small"
                  disabled={
                    factLoading ||
                    !fromDateTimeFormatted ||
                    !toDateTimeFormatted ||
                    fromDateTimeFormatted === "Invalid Date" ||
                    toDateTimeFormatted === "Invalid Date"
                  }
                  sx={{
                    borderRadius: "50px",
                    fontFamily: "'Host Grotesk', sans-serif",
                    fontSize: "13px",
                    fontWeight: 600,
                    textTransform: "none",
                    padding: "8px 24px",
                    minWidth: "160px",
                    background: factLoading
                      ? "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
                      : "linear-gradient(180deg, #4079f0 0%, #2a5fe6 100%)",
                    boxShadow: factLoading
                      ? "none"
                      : "0 4px 12px rgba(53, 114, 239, 0.35)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: factLoading
                        ? "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
                        : "linear-gradient(135deg, #2a5fe6 0%, #1d4ed8 100%)",
                      boxShadow: factLoading
                        ? "none"
                        : "0 6px 16px rgba(53, 114, 239, 0.45)",
                      transform: factLoading ? "none" : "translateY(-1px)",
                    },
                    "&:active": {
                      transform: factLoading ? "none" : "translateY(0px)",
                    },
                    "&:disabled": {
                      background:
                        "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  {factLoading ? "Generating..." : "Download Report"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="fn-control-panel">
          <div className="fn-input-wrapper">
            <div className="fn-textarea-container">
              <textarea
                className={`fn-claim-textarea ${
                  isWordLimitExceeded ? "word-limit-exceeded" : ""
                }`}
                placeholder="Enter a post to fact-check (40 words or less). Special characters are not permitted."
                value={input}
                onChange={handleInputChange}
                style={{
                  borderColor: isWordLimitExceeded ? "#ff4444" : undefined,
                  backgroundColor: isWordLimitExceeded ? "#fff5f5" : undefined,
                }}
              />
              {/* Combined word counter and error message */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "4px",
                }}
              >
                {/* Error message on the left */}
                {isWordLimitExceeded && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#ff4444",
                      fontFamily: "Host Grotesk, sans-serif",
                      fontWeight: isWordLimitExceeded ? "bold" : "normal",
                      flex: 1,
                    }}
                  >
                    Please enter 40 words or less
                  </div>
                )}

                {/* Word counter on the right */}
                <div
                  className="word-counter"
                  style={{
                    fontSize: "12px",
                    fontFamily: "Host Grotesk, sans-serif",
                    color: isWordLimitExceeded ? "#ff4444" : "#666",
                    fontWeight: isWordLimitExceeded ? "bold" : "normal",
                    marginLeft: "auto", // Push to right
                  }}
                >
                  {wordCount}/40 words
                </div>
              </div>
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <div className="fn-action-group">
                <div style={{ marginTop: "10px" }}>
                  <TranslateSwitch
                    onTranslateChange={handleTranslateChange}
                    translateValue={translateValue}
                  />
                </div>

                <button
                  className="fn-report-button"
                  onClick={analyzeFakeNews}
                  disabled={
                    fakeNewsLoading || isWordLimitExceeded || !input.trim()
                  }
                  style={{
                    opacity:
                      fakeNewsLoading || isWordLimitExceeded || !input.trim()
                        ? 0.6
                        : 1,
                    cursor:
                      fakeNewsLoading || isWordLimitExceeded || !input.trim()
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {fakeNewsLoading ? (
                    <div className="fn-loading-wrapper">
                      <div className="fn-spinner"></div>
                      <span>Analyzing your claim…</span>
                    </div>
                  ) : (
                    "CHECK!"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {loader || factLoading ? (
          <div
            className="three-body-problem"
            style={{
              height: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="three-body">
              <div className="three-body__dot"></div>
              <div className="three-body__dot"></div>
              <div className="three-body__dot"></div>
            </div>
          </div>
        ) : (
          <div>
            <div className="fn-cards-grid">
              {fakeNewsLoading && (
                <FakeNewsSkeletonCard loading={fakeNewsLoading} />
              )}
              {data.map((item, index) => {
                const isExpanded = expandedCards[item._id] || false;
                const currentImageIndex = 0;

                return (
                  <FakeNewsCard
                    key={item._id || index}
                    item={item}
                    index={index}
                    isExpanded={isExpanded}
                    currentImageIndex={currentImageIndex}
                    placeholder={placeholder}
                    onToggleExpansion={toggleCardExpansion}
                    onOpenModal={handleOpenModal} // for opening the modal
                    onDownloadReport={generateIndividualReport} // for generating reports
                    truncateToWordLimit={truncateToWordLimit}
                    formatDate={formatDate}
                  />
                );
              })}
            </div>
          </div>
        )}
        <FakeNewsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          item={selectedItem || {}}
          formatDate={formatDate}
          truncateToWordLimit={truncateToWordLimit}
          placeholder={placeholder}
        />
        <CustomPagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </main>
      <ReactProSidebar />
    </div>
  );
};

export default FakeNews;
